import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { OPTION_LABELS } from "@/components/kingsley/OptionGroup";

const EMPTY = { name: "", email: "", phone: "", address: "", message: "" };
const cls =
  "w-full border-b border-ink/25 bg-transparent pb-3 pt-1 font-display text-lg font-extralight tracking-tight text-ink outline-none transition-colors duration-300 placeholder:text-muted/80 focus:border-ink";

function Field({ label, k, form, update, ...rest }) {
  return (
    <label className="block">
      <span className="overline mb-3 block text-muted">{label}</span>
      <input data-testid={`reserve-${k}`} value={form[k]} onChange={update(k)} className={cls} {...rest} />
    </label>
  );
}

// Order-by-request flow, used for pieces with no online price yet.
export default function PieceRequest({ product, sel, optionKeys, measures, onDone }) {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return toast.error("Please add your name and email.");
    setSubmitting(true);
    try {
      const chosen = optionKeys.map((k) => `${OPTION_LABELS[k]}: ${sel[k] || "—"}`).join(" · ");
      const ship = `Ship to: ${form.address || "—"}${form.phone ? ` · Phone: ${form.phone}` : ""}`;
      const message = ["ORDER REQUEST", `Piece: ${product.name}`, chosen, measures, ship, "", form.message || "(no note)"]
        .filter(Boolean).join("\n");
      await api.post("/contact", { name: form.name, email: form.email, projectType: "Order request", message, section: "Kingsley" });
      toast.success("Thank you — your request is in. I’ll confirm the price and details shortly.");
      setForm(EMPTY);
      onDone();
    } catch {
      toast.error("Something went wrong. Please try again or email directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="kingsley-reserve-form" className="flex flex-col gap-8">
      <div className="grid gap-8 sm:grid-cols-2">
        <Field label="Your Name *" k="name" form={form} update={update} placeholder="Jane Doe" />
        <Field label="Email *" k="email" form={form} update={update} type="email" placeholder="you@email.com" />
      </div>
      <div className="grid gap-8 sm:grid-cols-2">
        <Field label="Phone" k="phone" form={form} update={update} placeholder="Optional" />
        <Field label="Shipping address" k="address" form={form} update={update} placeholder="Where should it go?" />
      </div>
      <Field label="Note" k="message" form={form} update={update} placeholder="Anything I should know…" />
      <button type="submit" data-testid="kingsley-reserve-submit" disabled={submitting}
        className="group mt-2 inline-flex w-fit items-center gap-3 border border-ink px-10 py-4 transition-colors duration-500 hover:bg-ink hover:text-paper disabled:opacity-50">
        <span className="overline">{submitting ? "Sending…" : "Request this piece"}</span>
        <span className="transition-transform duration-500 group-hover:translate-x-1.5">↗</span>
      </button>
      <p className="font-body text-xs leading-relaxed text-muted">
        Price on request — I’ll confirm the price and details, then send a secure link to pay.{" "}
        <Link to="/kingsley/fitting" className="link-underline">Book a fitting</Link> instead.
      </p>
    </form>
  );
}
